import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { supabase } from "../supabase";
import type { AppOptions, DashboardData, Department, PendingDashboardData, PendingScope, Summary } from "../types";
import DeskLogBoard from "./DeskLogBoard";
import PendingSummaryCard from "./PendingSummaryCard";

function localDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

const departments: Array<{id: Department; label: string}> = [
  {id: "ford", label: "Ford"},
  {id: "subaru", label: "Subaru"},
  {id: "new_commercial", label: "New Commercial"},
  {id: "used", label: "Used"}
];

function SummaryCards({title, summary}: {title: string; summary: Summary}) {
  return (
    <section className="panel">
      <div className="panel-heading">
        <div><span className="eyebrow">Live totals</span><h2>{title}</h2></div>
        <strong className="grand-total">{summary.total}</strong>
      </div>
      <div className="summary-grid">
        {departments.map((department) => <article key={department.id}><span>{department.label}</span><strong>{summary.departments[department.id] ?? 0}</strong></article>)}
        <article><span>Isuzu</span><strong>{summary.isuzu}</strong></article>
      </div>
    </section>
  );
}

const emptySummary: Summary = {
  total: 0,
  isuzu: 0,
  departments: {ford: 0, subaru: 0, new_commercial: 0, used: 0}
};

const emptyPending: PendingDashboardData = {
  totalOpen: 0,
  futureDeals: 0,
  callInsAwaitingApproval: 0,
  approvedNotDelivered: 0,
  depositsDue: 0,
  followUpsDue: 0,
  tomorrowsDeliveries: 0,
  closurePending: 0
};

export default function Dashboard({
  options,
  canManagePending,
  onOpenPending,
  onAddPending
}: {
  options: AppOptions;
  canManagePending: boolean;
  onOpenPending: (scope: PendingScope) => void;
  onAddPending: () => void;
}) {
  const [data, setData] = useState<DashboardData>({
    date: localDate(),
    month: localDate().slice(0, 7),
    today: emptySummary,
    monthToDate: emptySummary,
    deals: []
  });
  const [pending, setPending] = useState<PendingDashboardData>(emptyPending);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setError("");
    try {
      const [dashboard, pendingSummary] = await Promise.all([
        api.dashboard(localDate()),
        api.pendingDashboard(localDate())
      ]);
      setData(dashboard);
      setPending(pendingSummary);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Dashboard could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    const interval = window.setInterval(() => void refresh(), 30000);
    let timer: number | undefined;
    const channel = supabase
      .channel("desk-log-dashboard")
      .on("postgres_changes", {event: "*", schema: "public", table: "deals"}, () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => void refresh(), 250);
      })
      .on("postgres_changes", {event: "*", schema: "public", table: "pending_deals"}, () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => void refresh(), 250);
      })
      .subscribe();

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [refresh]);

  if (loading) return <div className="center-panel"><div className="loader" /><p>Loading live totals…</p></div>;

  return (
    <div className="stack">
      {error && <p className="error">{error}</p>}
      <div className="two-column">
        <SummaryCards title="Today’s Sales" summary={data.today} />
        <SummaryCards title="Month-to-Date" summary={data.monthToDate} />
      </div>
      <div className="dashboard-workspace">
        <div className="daily-tracker-area"><DeskLogBoard options={options} deals={data.deals} /></div>
        <PendingSummaryCard summary={pending} canManage={canManagePending} onOpen={onOpenPending} onAdd={onAddPending} />
      </div>
    </div>
  );
}
